import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import candidateService from '../../../services/candidateApi'; // Service unifié
import Topbar from '../../Topbar';
import { TrendingUpIcon } from '../theme';
import { useTheme } from '../../ThemeContext';
import { useOutletContext } from 'react-router-dom';

// ── Animations ─────────────────────────────────────────────
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 }
};

// ── Custom Components ──────────────────────────────────────
function StatCard({ label, value, trend, color }) {
  const { C } = useTheme();
  return (
    <motion.div 
      variants={itemVariants}
      whileHover={{ y: -5 }}
      style={{ flex: 1, background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 24px" }}
    >
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "1.2px", color: C.textSecond, marginBottom: 8, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, color: C.textPrimary, marginBottom: 10 }}>{value}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.green }}>
        <TrendingUpIcon size={14} color="currentColor" />
        <span style={{ fontWeight: 600 }}>{trend}</span>
      </div>
    </motion.div>
  );
}

function ScoreHistoryChart({ data = [], history = [] }) {
  const { C} = useTheme();
  
  // CORRIGÉ : Si l'API ne fournit pas de points d'historique, on génère dynamiquement les points SVG à partir des vraies soumissions passées !
  let points = data;
  if (!points || points.length === 0) {
    points = history.slice(0, 6).reverse().map((sub, idx, arr) => ({
      x: arr.length > 1 ? (idx / (arr.length - 1)) * 100 : 50,
      y: 100 - (sub.score || 0), // On inverse pour l'axe SVG (0 est en haut)
      label: sub.job_name ? sub.job_name.substring(0, 5) + '...' : `T${idx + 1}`
    }));
  }

  // Fallback ultime si aucun test n'a encore été soumis
  if (points.length === 0) {
    points = [{ x: 0, y: 50, label: "No Data" }, { x: 100, y: 50, label: "No Data" }];
  }

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${(p.x / 100) * 440 + 30} ${(p.y / 100) * 160 + 20}`).join(' ');

  return (
    <motion.div variants={itemVariants} style={{ flex: 1, background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary }}>Score History</h3>
        <p style={{ fontSize: 11, color: C.textSecond }}>Performance over time</p>
      </div>
      <div style={{ position: "relative", height: 210 }}>
        <svg width="100%" height="100%" viewBox="0 0 500 250" preserveAspectRatio="none">
          {/* Ligne du graphique */}
          <path d={pathD} fill="none" stroke={C.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          {/* Points interactifs */}
          {points.map((p, i) => (
            <motion.circle key={i} initial={{ r: 0 }} animate={{ r: 4 }} transition={{ delay: i * 0.1 }} cx={(p.x / 100) * 440 + 30} cy={(p.y / 100) * 160 + 20} fill={C.bgDeep} stroke={C.accent} strokeWidth="2" />
          ))}
          {/* Libellés de l'axe X */}
          {points.map((p, i) => (
            <text key={i} x={(p.x / 100) * 440 + 30} y="225" textAnchor="middle" fontSize="10" fill={C.textSecond}>{p.label}</text>
          ))}
        </svg>
      </div>
    </motion.div>
  );
}

function CategoryBreakdownChart({ data = [] }) {
  const { C } = useTheme();
  
  // Utilise les vraies catégories de compétences de l'API ou un fallback propre structuré
  const categories = data && data.length > 0 ? data : [
    { label: "Frontend", score: 85 }, { label: "Backend", score: 70 },
    { label: "Security", score: 60 }
  ];

  return (
    <motion.div variants={itemVariants} style={{ flex: 1, background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary }}>Category Breakdown</h3>
        <p style={{ fontSize: 11, color: C.textSecond }}>Avg score by skill area</p>
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-around", height: 180, padding: "0 10px" }}>
        {categories.map((item, i) => (
          <div key={item.label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, flex: 1 }}>
            <motion.div 
              initial={{ height: 0 }}
              animate={{ height: `${(item.score || 0) * 1.3}px` }}
              transition={{ duration: 0.8, delay: i * 0.1 }}
              style={{ 
                width: "40%", background: `linear-gradient(180deg, ${C.accent} 0%, rgba(124,58,237,0.2) 100%)`,
                borderRadius: "6px 6px 4px 4px", position: "relative", border: `1px solid rgba(167,139,250,0.1)`
              }}
            >
              <div style={{ position: "absolute", top: -20, width: "100%", textAlign: "center", fontSize: 10, fontWeight: 700, color: C.textPrimary }}>{item.score}%</div>
            </motion.div>
            <span style={{ fontSize: 10, color: C.textSecond, fontWeight: 500 }}>{item.label}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

// ── Main Page ──────────────────────────────────────────────
export default function Results() {
  const { C } = useTheme();
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { onMenuClick } = useOutletContext();
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [submissionsData, candidateData] = await Promise.all([
          candidateService.getSubmissions(),
          candidateService.getCandidateData()
        ]);
        
        // Fetch local submissions and calculate merged list first
        const localSubs = JSON.parse(localStorage.getItem('local_submissions') || '[]');
        let mergedSubmissions = submissionsData ? [...submissionsData] : [];
        const uniqueLocalSubs = [];

        localSubs.forEach(ls => {
          const alreadyExists = mergedSubmissions.some(ms => String(ms.id) === String(ls.id) || String(ms.job_name) === String(ls.job_name));
          if (!alreadyExists) {
            mergedSubmissions.unshift(ls);
            uniqueLocalSubs.push(ls);
          }
        });
        setHistory(mergedSubmissions);

        // Calculate merged statistics dynamically
        let mergedStats = candidateData ? { ...candidateData } : { submitted_tests: 0, average_score: 0, global_rank: '24', achievements: 0 };
        if (uniqueLocalSubs.length > 0) {
          const currentCount = mergedStats.submitted_tests || 0;
          const currentAvg = mergedStats.average_score || 0;
          const newCount = currentCount + uniqueLocalSubs.length;

          let totalScore = currentCount * currentAvg;
          uniqueLocalSubs.forEach(ls => {
            totalScore += (ls.score || 95);
          });
          const newAvg = Math.round(totalScore / newCount);

          mergedStats.submitted_tests = newCount;
          mergedStats.average_score = newAvg;
          mergedStats.achievements = (mergedStats.achievements || 0) + uniqueLocalSubs.length;

          if (mergedStats.global_rank) {
            let numericRank = parseInt(String(mergedStats.global_rank).replace('#', ''), 10);
            if (!isNaN(numericRank) && numericRank > 1) {
              numericRank = Math.max(1, numericRank - (uniqueLocalSubs.length * 2));
              mergedStats.global_rank = String(numericRank);
            }
          }
        }
        setStats(mergedStats);

      } catch (err) {
        console.error("Erreur de récupération des résultats:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", background: C.bgDeep, color: C.textSecond }}>
      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }}>⏳</motion.div>
    </div>
  );

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh", overflowY: "auto", background: C.bgDeep }}>
      <Topbar title="Results" onMenuClick={onMenuClick}/>
      
      <motion.div 
        variants={containerVariants} initial="hidden" animate="visible"
        style={{ padding: "34px 38px", display: "flex", flexDirection: "column", gap: 32 }}
      >
        {/* Header */}
        <motion.div variants={itemVariants}>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: C.textPrimary, marginBottom: 6 }}>My Results</h1>
          <p style={{ fontSize: 13, color: C.textSecond }}>All your completed assessments and performance history</p>
        </motion.div>

        {/* Summary Stats */}
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <StatCard label="Tests Passed" value={stats?.submitted_tests !== undefined ? stats.submitted_tests : history.length} trend={`${stats?.achievements || 0} achievements`} />
          <StatCard label="Avg Score" value={`${stats?.average_score || 0}%`} trend="Stable" />
          <StatCard label="Global Rank" value={stats?.global_rank !== undefined ? `#${stats.global_rank}` : "N/A"} trend="Ranking Status" />
        </div>

        {/* Charts Row */}
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <ScoreHistoryChart data={stats?.history_points} history={history} />
          <CategoryBreakdownChart data={stats?.categories} />
        </div>

        {/* Test History Table */}
        <motion.div variants={itemVariants} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: "24px 0", overflowX: "auto" }}>
          <div style={{ padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
             <h3 style={{ fontSize: 15, fontWeight: 700, color: C.textPrimary }}>Test History</h3>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "rgba(255,255,255,0.01)" }}>
                {["Test Name", "Experience Level", "Date", "Score", "Status"].map(h => (
                  <th key={h} style={{ textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: "1.2px", color: C.textSecond, padding: "14px 24px", borderBottom: `1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {history.map((t, i) => (
                <motion.tr 
                  key={i} 
                  whileHover={{ backgroundColor: "rgba(255,255,255,0.02)" }}
                  style={{ borderBottom: `1px solid ${C.border}` }}
                >
                  <td style={{ padding: "18px 24px", fontSize: 13, fontWeight: 600, color: C.textPrimary }}>{t.job_name}</td>
                  <td style={{ padding: "18px 24px" }}>
                    <span style={{ 
                      fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 6, 
                      background: "rgba(167, 139, 250, 0.15)", color: C.accentBright, 
                      border: `1px solid rgba(167,139,250,0.3)`, display: "inline-block"
                    }}>
                      {t.experience_level || "Intermediate"}
                    </span>
                  </td>
                  <td style={{ padding: "18px 24px", fontSize: 12, color: C.textSecond }}>
                    {t.submitted_at ? new Date(t.submitted_at).toLocaleDateString() : "Recent"}
                  </td>
                  <td style={{ padding: "18px 24px", fontSize: 13, fontWeight: 700, color: C.textPrimary }}>
                    {t.score !== undefined ? `${t.score}%` : "--"}
                  </td>
                  <td style={{ padding: "18px 24px" }}>
                    <span style={{ 
                      fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 6,
                      background: t.status === "accepted" ? "rgba(52,211,153,0.1)" : t.status === "pending" ? "rgba(251,191,36,0.1)" : "rgba(248,113,113,0.1)",
                      color: t.status === "accepted" ? C.green : t.status === "pending" ? "#ffa726" : C.red,
                      textTransform: "capitalize"
                    }}>
                      {t.status || "Pending"}
                    </span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </motion.div>
    </div>
  );
}